import type { AxiosInstance } from 'axios';
import axios from 'axios';
import authService from './auth.service';
import { toText } from '../lib/toText';
import type {
  CaseDataUpsertBody,
  CaseDetail,
  CaseDetailFull,
  CaseListResponse,
  CaseStartBody,
  ListCasesFilters,
  TransitionBody,
  TransitionResponse,
} from '../types/workflow';
import type { UploadProgress } from '../lib/uploadProgress';

/** The three evidence items enhanced due diligence can require. */
export type EDDEvidenceType = 'source_of_funds' | 'tax_certification' | 'foreign_id_screening';

export interface EDDEvidenceItem {
  evidence_type: EDDEvidenceType;
  status: 'provided' | 'waived' | null;
  document_reference?: string | null;
  note?: string | null;
  recorded_by?: string | null;
  recorded_at?: string | null;
}

export interface EDDChecklist {
  /** False when this case's risk reasons require nothing. */
  edd_required: boolean;
  required: EDDEvidenceType[];
  items: EDDEvidenceItem[];
  /** Required and not yet provided or waived — what blocks the approval. */
  outstanding: EDDEvidenceType[];
}

export interface EDDEvidenceIn {
  evidence_type: EDDEvidenceType;
  status: 'provided' | 'waived';
  document_reference?: string | null;
  note?: string | null;
}


export interface DocumentUploadResult {
  document_id: string;
  storage_path: string;
  file_hash: string;
  size_bytes: number;
  mime_type: string;
}

/** One advisory KYC-review check result. `status`: ok | warn | fail | na. */
export interface KycSignal {
  code: string;
  label: string;
  status: 'ok' | 'warn' | 'fail' | 'na';
  detail: string;
}

/** Aggregate KYC review-assistant verdict (no PII — verdicts only). */
export interface KycReview {
  risk_level: 'low' | 'medium' | 'high';
  counts: Record<string, number>;
  signals: KycSignal[];
}

/** One required-but-missing onboarding item the client still owes. */
export interface PendingItem {
  field_key: string;
  label: string;
  control_type: string;
}

/** Required-but-missing items for a case's current state (self-service). */
export interface PendingItems {
  state_slug: string;
  can_complete: boolean;
  items: PendingItem[];
}

class WorkflowService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  /**
   * GET /api/v1/workflow/cases — paginated queue.
   *
   * Repeated params (`state_slug`, `status`) are sent by passing arrays in
   * `params` — axios serializes them as `?state_slug=a&state_slug=b` when
   * `paramsSerializer` uses repeat mode. We use the explicit serializer to
   * avoid axios's default `?state_slug[]=a` style which FastAPI doesn't
   * parse the same way.
   */
  async listCases(filters: ListCasesFilters = {}): Promise<CaseListResponse> {
    const res = await this.api.get<CaseListResponse>('/api/v1/workflow/cases', {
      params: filters,
      paramsSerializer: (params) => {
        const parts: string[] = [];
        for (const [key, value] of Object.entries(params)) {
          if (value === undefined || value === null) continue;
          if (Array.isArray(value)) {
            for (const v of value) {
              if (v === undefined || v === null) continue;
              parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
            }
          } else {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(toText(value))}`);
          }
        }
        return parts.join('&');
      },
    });
    return res.data;
  }

  /**
   * GET /api/v1/workflow/cases/{id} — single-case detail with denormalized
   * member + assignee info. Used by the staff review page; clients can
   * call it too but the server only returns their own cases.
   */
  async getCase(caseId: string): Promise<CaseDetail> {
    const res = await this.api.get<CaseDetail>(`/api/v1/workflow/cases/${caseId}`);
    return res.data;
  }

  /**
   * GET /api/v1/workflow/cases/{id} — same endpoint as `getCase` but
   * typed against the FAT shape that includes `fields` (current state's
   * form definition) and `data` (captured values). Used by the client
   * onboarding wizard, where rendering depends on both.
   */
  async getCaseFull(caseId: string): Promise<CaseDetailFull> {
    const res = await this.api.get<CaseDetailFull>(`/api/v1/workflow/cases/${caseId}`);
    return res.data;
  }

  /**
   * GET /api/v1/workflow/cases/{id}/pending-items — required fields/documents
   * the case still needs for its current state. Powers the dashboard
   * self-service surface so the client sees exactly what's outstanding and
   * can complete it without waiting for an agent.
   */
  async getPendingItems(caseId: string): Promise<PendingItems> {
    const res = await this.api.get<PendingItems>(
      `/api/v1/workflow/cases/${caseId}/pending-items`,
    );
    return res.data;
  }

  /**
   * GET /api/v1/workflow/cases/{id}/review-assistant — advisory KYC review
   * signals for staff. Deterministic, in-house fraud/inconsistency checks (age,
   * CURP/RFC structure + identity consistency, CURP↔RFC cross-consistency, name
   * match, document validity). Presence/completeness is enforced upstream at the
   * SUBMIT gate, not re-checked here. Server returns ONLY verdicts (no PII),
   * staff-only.
   */
  async getReviewAssistant(caseId: string): Promise<KycReview> {
    const res = await this.api.get<KycReview>(
      `/api/v1/workflow/cases/${caseId}/review-assistant`,
    );
    return res.data;
  }

  /**
   * POST /api/v1/workflow/onboarding/resume-or-start — return the
   * caller's active case for a workflow slug, creating one (in the
   * initial state) if none exists. The frontend never has to know
   * the product_id — the server resolves it from the assignment.
   */
  async resumeOrStartOnboarding(workflowSlug: string): Promise<{ case_id: string; created: boolean }> {
    const res = await this.api.post<{ case_id: string; created: boolean }>(
      '/api/v1/workflow/onboarding/resume-or-start',
      { workflow_slug: workflowSlug },
    );
    return res.data;
  }

  /**
   * POST /api/v1/workflow/cases — create a case for a (user, product).
   * Returns `{ case_id }`. Server enforces a unique-active-case-per-
   * (user, product, workflow) constraint, so a duplicate call comes
   * back as 409 Conflict with the existing case to be resumed.
   */
  async startCase(body: CaseStartBody): Promise<{ case_id: string }> {
    const res = await this.api.post<{ case_id: string }>(
      '/api/v1/workflow/cases',
      body,
    );
    return res.data;
  }

  /**
   * PUT /api/v1/workflow/cases/{id}/data — partial save of field
   * values for the current state. Sensitive fields are encrypted
   * server-side via Cloud KMS envelope encryption. Used by the
   * "Save progress" button so the client can step away mid-form.
   */
  async saveCaseData(caseId: string, body: CaseDataUpsertBody): Promise<{ written: number }> {
    const res = await this.api.put<{ written: number }>(
      `/api/v1/workflow/cases/${caseId}/data`,
      body,
    );
    return res.data;
  }

  /**
   * Per-field review (agent / manager): flip a captured data field
   * to approved or rejected. `rejection_reason` is REQUIRED when
   * status='rejected' and FORBIDDEN otherwise — the backend
   * enforces both server-side, mirrored by the case_data CHECK
   * constraint in the schema.
   *
   * Available only when the case is in `under_review` or
   * `information_required`; outside that window the backend 409s.
   */
  async reviewCaseDataField(
    caseId: string,
    fieldKey: string,
    body: {
      review_status: 'approved' | 'rejected';
      rejection_reason?: string | null;
    },
  ): Promise<{ ok: true; review_status: string }> {
    const res = await this.api.patch<{ ok: true; review_status: string }>(
      `/api/v1/workflow/cases/${caseId}/data/${encodeURIComponent(fieldKey)}/review`,
      body,
    );
    return res.data;
  }

  /**
   * Per-document review (agent / manager). Maps to the existing
   * workflow.document_status enum: `valid` approves, `invalid`
   * rejects with a reason. Same review window as field review.
   */
  async reviewCaseDocument(
    caseId: string,
    documentId: string,
    body: {
      status: 'valid' | 'invalid';
      rejection_reason?: string | null;
    },
  ): Promise<{ ok: true; status: string }> {
    const res = await this.api.patch<{ ok: true; status: string }>(
      `/api/v1/workflow/cases/${caseId}/documents/${documentId}/review`,
      body,
    );
    return res.data;
  }

  /**
   * Two-phase KYC document upload:
   *   1. POST upload-presign → server returns a short-lived URL
   *      bound to the case + field + MIME type and the object key.
   *   2. PUT the file bytes directly to object storage (NO auth
   *      header — the presigned URL carries its own signature; our
   *      Bearer token would be rejected by S3 / MinIO).
   *   3. POST upload-confirm → server HEADs the object, validates
   *      size + MIME, persists `case_documents` and the pointer in
   *      `case_data`.
   *
   * `onProgress` fires with values 0..1 during the bucket PUT so the
   * caller can render a progress bar.
   */
  async uploadKycDocument(
    caseId: string,
    fieldKey: string,
    file: File,
    options?: {
      onProgress?: (progress: UploadProgress) => void;
      /** Pass `controller.signal` here; abort() raises a CanceledError
       *  from axios so the caller can distinguish "user cancelled" from
       *  "network failed". */
      signal?: AbortSignal;
    },
  ): Promise<DocumentUploadResult> {
    const { onProgress, signal } = options ?? {};

    const presign = await this.api.post<{
      upload_url: string;
      object_key: string;
      expires_in_seconds: number;
    }>(
      `/api/v1/workflow/cases/${caseId}/documents/${fieldKey}/upload-presign`,
      { mime_type: file.type },
      { signal },
    );

    // Direct PUT to the bucket. axios bare instance — bypasses our
    // request interceptor that would otherwise attach the Bearer
    // token (which S3 would reject).
    await axios.put(presign.data.upload_url, file, {
      headers: { 'Content-Type': file.type },
      signal,
      onUploadProgress: (e) => {
        if (!onProgress) return;
        const total = e.total ?? file.size;
        const loaded = e.loaded;
        const fraction = total > 0 ? Math.min(1, loaded / total) : 0;
        onProgress({ fraction, loaded, total });
      },
    });

    const confirm = await this.api.post<DocumentUploadResult>(
      `/api/v1/workflow/cases/${caseId}/documents/${fieldKey}/upload-confirm`,
      { object_key: presign.data.object_key },
      { signal },
    );
    return confirm.data;
  }

  /** Short-lived presigned GET URL for a previously uploaded
   *  workflow document. Used to render a "Download / Preview" link
   *  on uploaded files in the wizard and (later) on the staff
   *  review page. */
  async getDocumentDownloadUrl(caseId: string, documentId: string): Promise<string> {
    const res = await this.api.get<{ download_url: string }>(
      `/api/v1/workflow/cases/${caseId}/documents/${documentId}/download`,
    );
    return res.data.download_url;
  }

  /** Capture or correct the expiry on a previously-uploaded document.
   *  All three params are optional with cascade rules (day requires
   *  month, month requires year). Passing all three null clears the
   *  expiry. Backend resolves to a conservative end-of-period instant
   *  and remembers the granularity for UI phrasing. */
  async updateDocumentExpiry(
    caseId: string,
    documentId: string,
    body: {
      expiry_year?: number | null;
      expiry_month?: number | null;
      expiry_day?: number | null;
      // Relative-validity documents (proof of address, SAT certificate) send
      // the issue date instead; the backend computes the expiry from the window.
      issued_year?: number | null;
      issued_month?: number | null;
      issued_day?: number | null;
    },
  ): Promise<{ document_id: string; expires_at: string | null; expiry_granularity: 'exact' | 'month' | 'year' | null }> {
    const res = await this.api.patch<{
      document_id: string;
      expires_at: string | null;
      expiry_granularity: 'exact' | 'month' | 'year' | null;
    }>(
      `/api/v1/workflow/cases/${caseId}/documents/${documentId}/expiry`,
      body,
    );
    return res.data;
  }

  /**
   * POST /api/v1/workflow/cases/{id}/transition — apply an FSM event.
   *
   * Privacy contract:
   *   - When transitioning to a client-facing state (`returned_to_client`),
   *     `client_visible_notes` is REQUIRED.
   *   - Otherwise, `client_visible_notes` MUST be omitted; the server
   *     rejects the request with 400 if present.
   */
  async applyTransition(caseId: string, body: TransitionBody): Promise<TransitionResponse> {
    const res = await this.api.post<TransitionResponse>(
      `/api/v1/workflow/cases/${caseId}/transition`,
      body,
    );
    return res.data;
  }

  /**
   * GET /api/v1/workflow/catalogs/{slug}/items?parent_code=...
   *
   * Lazy-load items for a catalog, optionally filtered by a parent
   * item's code. Used by cascading selects (e.g. state_residence
   * filtered by country_residence) where shipping the full hierarchy
   * eagerly would bloat every form payload.
   */
  async listCatalogItems(
    slug: string,
    parentCode?: string,
  ): Promise<Array<{ code: string; label: string; metadata: unknown }>> {
    const res = await this.api.get<{
      items: Array<{ code: string; label: string; metadata: unknown }>;
    }>(`/api/v1/workflow/catalogs/${encodeURIComponent(slug)}/items`, {
      params: parentCode ? { parent_code: parentCode } : {},
    });
    return res.data.items;
  }

  /**
   * Fetch the portal's placeholder RFC. It is a transient stand-in for a foreign national
   * living in Mexico until SAT issues the client's real one (the dashboard then nags about
   * replacing it). There is no placeholder CURP: a foreign resident gives the CURP printed
   * on the INM residence card.
   *
   * The value comes from `platform.settings` and is cacheable for the session.
   */
  async getKycPlaceholders(): Promise<{ rfc: string }> {
    const res = await this.api.get<{ rfc: string }>('/api/v1/workflow/kyc/placeholders');
    return res.data;
  }

  /**
   * POST /api/v1/workflow/kyc/curp/generate
   *
   * Derives a CURP from the user's name, date of birth, sex, and birth
   * state using the SEGOB structural algorithm. The returned CURP uses
   * '0' for the differentiation digit; RENAPO assigns the final one on
   * official registration. Returns null on server error so the field
   * stays editable rather than blocking the form.
   */
  async generateCurp(params: {
    primer_apellido: string;
    nombre: string;
    fecha_nacimiento: string;
    sexo: 'H' | 'M';
    estado_nacimiento: string;
    segundo_apellido?: string;
  }): Promise<{ curp: string; valid: boolean } | null> {
    try {
      const res = await this.api.post<{ curp: string; valid: boolean }>(
        '/api/v1/workflow/kyc/curp/generate',
        params,
      );
      return res.data;
    } catch {
      return null;
    }
  }

  /**
   * GET /api/v1/workflow/kyc/curp/validate/{curp}
   *
   * Structural CURP validation (regex + verification digit). No network
   * calls beyond the API — purely local computation on the server.
   */
  // ── Enhanced due diligence (high-risk cases) ────────────────────────
  /** The case's EDD checklist: which items the case's own risk reasons require,
   *  and whether each is provided or waived. Staff read. */
  async eddEvidence(caseId: string): Promise<EDDChecklist> {
    const res = await this.api.get<EDDChecklist>(
      `/api/v1/workflow/cases/${caseId}/edd-evidence`,
    );
    return res.data;
  }

  /** Record one item. Manager / super_admin, audited. A waiver carries the
   *  reason it was waived — that note is the decision. */
  async recordEddEvidence(caseId: string, body: EDDEvidenceIn): Promise<void> {
    await this.api.post(`/api/v1/workflow/cases/${caseId}/edd-evidence`, body);
  }

  async validateCurp(curp: string): Promise<{ curp: string; valid: boolean; reason: string | null }> {
    const res = await this.api.get<{ curp: string; valid: boolean; reason: string | null }>(
      `/api/v1/workflow/kyc/curp/validate/${encodeURIComponent(curp)}`,
    );
    return res.data;
  }
}

export default new WorkflowService();
